import axios from "axios";
import { API } from "@/lib/api";

// Converts the Base64URL public key into the Uint8Array the browser expects
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function getServiceWorkerRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg =
      (await navigator.serviceWorker.getRegistration()) ||
      (await navigator.serviceWorker.register("/sw.js"));
    await navigator.serviceWorker.ready;
    return reg;
  } catch (e) {
    console.error("SW registration failed", e);
    return null;
  }
}

export async function getPushStatus() {
  if (!isPushSupported()) return { supported: false, subscribed: false, permission: "default" };
  const reg = await getServiceWorkerRegistration();
  if (!reg) return { supported: false, subscribed: false, permission: Notification.permission };
  const sub = await reg.pushManager.getSubscription();
  return {
    supported: true,
    subscribed: !!sub,
    permission: Notification.permission,
  };
}

export async function enablePush() {
  if (!isPushSupported()) {
    throw new Error("Push notifications aren't supported on this device.");
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission was blocked.");
  }

  const reg = await getServiceWorkerRegistration();
  if (!reg) throw new Error("Service worker unavailable.");

  const { data } = await axios.get(`${API}/push/vapid-public-key`);
  if (!data?.public_key) throw new Error("Server push key unavailable.");

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    });
  }

  const subJson = sub.toJSON();
  await axios.post(`${API}/push/subscribe`, {
    endpoint: subJson.endpoint,
    keys: subJson.keys,
    expirationTime: subJson.expirationTime ?? null,
  });
  return true;
}

export async function disablePush() {
  const reg = await getServiceWorkerRegistration();
  if (reg) {
    const sub = await reg.pushManager.getSubscription();
    if (sub) await sub.unsubscribe();
  }
  try {
    await axios.delete(`${API}/push/unsubscribe`);
  } catch (e) {
    // ignore — may not be authenticated or already removed
  }
  return true;
}

export async function sendTestPush() {
  const { data } = await axios.post(`${API}/push/test`);
  return data;
}

/**
 * Silently re-subscribe the device if browser permission is already granted.
 *
 * iOS Safari and (less often) Chrome quietly invalidate PWA push
 * subscriptions after the app sits closed for a while, after OS updates,
 * after reinstalling the PWA, etc. The server prunes any subscription
 * the next time a push to it returns 404/410. End result: users stop
 * getting notifications without ever toggling anything off.
 *
 * This helper runs on every app load. If permission is "granted" but
 * there's no active subscription, OR the server's last record of it
 * doesn't exist, we silently re-subscribe and POST the fresh details
 * to /push/subscribe. Users never see a prompt because they previously
 * granted permission.
 *
 * Returns:
 *   { healed: bool, reason: string }
 */
export async function autoHealPushSubscription() {
  try {
    if (!isPushSupported()) return { healed: false, reason: "unsupported" };
    if (Notification.permission !== "granted") {
      return { healed: false, reason: `permission=${Notification.permission}` };
    }
    const reg = await getServiceWorkerRegistration();
    if (!reg) return { healed: false, reason: "no-sw" };

    let sub = await reg.pushManager.getSubscription();
    let resubscribed = false;
    if (!sub) {
      // Permission still granted but the subscription got nuked. Recreate it.
      const { data } = await axios.get(`${API}/push/vapid-public-key`);
      if (!data?.public_key) return { healed: false, reason: "no-vapid-key" };
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      resubscribed = true;
    }

    // Re-POST the subscription to the backend on every app load. The
    // /push/subscribe endpoint is idempotent (upsert on user_id) so this
    // is cheap and guarantees the server always knows the latest endpoint.
    const subJson = sub.toJSON();
    try {
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
        expirationTime: subJson.expirationTime ?? null,
      });
    } catch (e) {
      // Likely 401 because the user isn't authenticated yet — fine, the
      // subscription is still valid locally; we'll resync on the next load.
      return { healed: resubscribed, reason: `server-sync-skipped:${e.response?.status || "unknown"}` };
    }
    return { healed: resubscribed, reason: resubscribed ? "resubscribed" : "synced" };
  } catch (e) {
    console.warn("[push auto-heal] failed:", e?.message || e);
    return { healed: false, reason: e?.message || "exception" };
  }
}
